import React, { useEffect, useState, useMemo } from 'react';
import { api, moneyFull, pct } from '../lib/api';
import { SectionHeader, Callout } from '../components/UI';
import AiMarkdown from '../components/AiMarkdown';
import { Line, LineChart, ResponsiveContainer } from 'recharts';
import {
  RefreshCw, ArrowUp, ArrowDown, ArrowRight,
  CheckCircle2, AlertTriangle, AlertOctagon, ShieldCheck, Activity, Info, BookOpen, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

// Account → brand accent border color. Matches Joey's spec exactly.
const ACCOUNT_ACCENT = {
  Income: 'border-indie-navy',
  Profit: 'border-indie-evergreen',
  Owner: 'border-indie-cobalt',
  Tax: 'border-indie-yellow',
  OpEx: 'border-indie-ink',
};

const ACCOUNT_SPARK = {
  Income: '#141B4D',
  Profit: '#006747',
  Owner: '#1D57A5',
  Tax: '#FFD100',
  OpEx: '#101820',
};

const ACCOUNT_LABEL = {
  Income: 'Income',
  Profit: 'Profit',
  Owner: 'Owner Pay',
  Tax: 'Tax',
  OpEx: 'OpEx',
};

const ORDER = ['Income', 'Profit', 'Owner', 'Tax', 'OpEx'];

function relativeHours(iso) {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return (Date.now() - t.getTime()) / 3600000;
}

function freshLabel(iso) {
  const h = relativeHours(iso);
  if (h == null) return 'never';
  if (h < 1) return 'just now';
  if (h < 24) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function ProfitFirst() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchData = () => {
    setLoading(true);
    api.get('/profit-first')
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load Profit First data.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/integrations/profit-first/sync');
      if (r.data.ok) {
        toast.success('Synced from sheet.');
        fetchData();
      } else {
        toast.error(`Sync failed: ${r.data.error || 'unknown'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  // Memoize the latest note relative-time string so it doesn't re-render every paint.
  const lastSyncLabel = useMemo(() => {
    if (!data?.notes?.length) return 'never';
    return freshLabel(data.notes[0].date);
  }, [data]);

  if (loading) return <div className="label-editorial">Loading Profit First…</div>;
  if (!data) return null;

  const cfg = data.config || {};
  const activeTier = data.active_tier || {};
  const tapCap = data.tap_cap_rows || [];
  const latest = data.latest_balances || {};
  const sparklines = data.sparklines || {};
  const preview = data.allocation_preview || {};
  const tax = data.tax_reserve || {};
  const tier = data.tier_progress || {};
  const score = data.health_score || {};
  const allocations = (data.allocations || []).slice(-8).reverse();

  const tierBand = activeTier.real_revenue_floor != null
    ? `Real revenue $${(activeTier.real_revenue_floor / 1000000).toFixed(activeTier.real_revenue_floor >= 1000000 ? 1 : 2).replace(/\.0$/, '')}M–$${(activeTier.real_revenue_ceiling / 1000000).toFixed(activeTier.real_revenue_ceiling >= 1000000 ? 0 : 2)}M`
    : '';

  return (
    <div className="space-y-8">
      {/* Header band */}
      <div data-testid="pf-header" className="bg-indie-navy text-indie-cream relative overflow-hidden">
        <div className="px-8 pt-7 pb-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="label-editorial-cream">Profit First</div>
              <div className="text-3xl lg:text-4xl font-bold text-white tracking-tight mt-1">Allocation discipline · live</div>
              <div className="text-sm text-indie-cream/70 mt-3 flex flex-wrap gap-x-5 gap-y-1">
                <span>Tier <span className="font-semibold text-white">{cfg.current_tier || '—'}</span></span>
                <span className="text-indie-cream/40">·</span>
                <span>{tierBand}</span>
                <span className="text-indie-cream/40">·</span>
                <span>Last sheet update: <span className="font-semibold text-white">{lastSyncLabel}</span></span>
              </div>
            </div>
            <div className="flex items-center gap-2 no-print">
              <button
                type="button"
                onClick={handleSync}
                disabled={syncing}
                data-testid="pf-sync-btn"
                className="inline-flex items-center gap-2 bg-indie-cream/10 hover:bg-indie-cream/20 text-indie-cream border border-indie-cream/20 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold disabled:opacity-50"
              >
                <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync now'}
              </button>
              <button
                type="button"
                onClick={() => window.print()}
                data-testid="pf-print-btn"
                className="inline-flex items-center gap-2 bg-indie-yellow text-indie-ink font-semibold uppercase tracking-wider text-xs px-3 py-1.5 hover:bg-indie-yellow/85"
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
        <div className="h-1.5 bg-indie-yellow" />
      </div>

      {/* Row 1 — 5 KPI cards */}
      <div data-testid="pf-kpi-row" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {ORDER.map((code) => {
          const b = latest[code] || {};
          const series = (sparklines[code] || []).map((p) => ({ v: p.balance, d: p.date }));
          const stale = (b.data_freshness_hours || 0) > 48;
          return (
            <div
              key={code}
              data-testid={`pf-kpi-${code.toLowerCase()}`}
              className={`bg-white border-t-4 ${ACCOUNT_ACCENT[code]} p-5 flex flex-col gap-1.5 relative`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="label-editorial">{ACCOUNT_LABEL[code]}</div>
                {stale && (
                  <span title={`${b.data_freshness_hours}h since QBO refresh`} className="text-indie-yellow">
                    <AlertTriangle size={13} />
                  </span>
                )}
              </div>
              <div className="text-2xl lg:text-[28px] font-semibold text-indie-navy num-tabular tracking-tight">
                {moneyFull(b.balance)}
              </div>
              <div className="text-xs text-indie-ink/65 flex items-center gap-2">
                <span>{b.pct_of_total != null ? pct(b.pct_of_total, 1) : '—'} of total</span>
                {b.delta_7d != null && (
                  <span className={`flex items-center gap-0.5 num-tabular ${b.delta_7d >= 0 ? 'text-indie-evergreen' : 'text-indie-neg-dark'}`}>
                    {b.delta_7d >= 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                    {moneyFull(Math.abs(b.delta_7d))} / 7d
                  </span>
                )}
              </div>
              {series.length > 1 && (
                <div className="h-9 mt-1.5 min-w-[40px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
                      <Line type="monotone" dataKey="v" stroke={ACCOUNT_SPARK[code]} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              {series.length <= 1 && (
                <div className="h-9 mt-1.5 flex items-end text-[10px] text-indie-ink/35 italic">
                  90-day trend builds as Viktor logs daily balances
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Disclaimer band — balances are QBO-derived, not live Relay. */}
      <div data-testid="pf-balance-disclaimer" className="-mt-3 bg-indie-cream border-l-4 border-indie-yellow px-4 py-3 flex items-start gap-3 text-xs text-indie-ink/75 leading-relaxed">
        <Info size={14} className="text-indie-yellow flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold text-indie-navy">Heads up:</span> these balances come from QuickBooks via Viktor's daily sync,
          not directly from Relay. Expect a small gap from the live Relay account values — QBO records transactions on a
          slight lag (deposits clear, sweeps post, fees hit) so the totals here trail real-time by 24–48 hours.
          For exact bank balances, check Relay directly.
        </div>
      </div>

      {/* Revenue context — what the two top-of-sheet revenue numbers mean. */}
      <RevenueContextCard
        forecast={cfg.forecast_annual_revenue}
        ttm={cfg.ttm_real_revenue_estimate}
        currentTier={cfg.current_tier}
        tierFloor={activeTier.real_revenue_floor}
        tierCeiling={activeTier.real_revenue_ceiling}
        nextTierFloor={tier.next_tier_floor}
        nextTierLetter={tier.next_tier}
        taxRate={cfg.quarterly_tax_rate}
      />

      {/* Tier & allocation strategy — the dominant card. Recommendations
          land here so the team knows what to actually do. */}
      <TierStrategyCard
        tier={tier}
        tiers={data.tiers || {}}
        currentTier={cfg.current_tier}
        tapCap={tapCap}
        latestBalances={latest}
        quarterlyTaxRate={cfg.quarterly_tax_rate}
        taxReserve={tax}
      />

      {/* Mike Michalowicz coach — on-demand Claude-powered note. */}
      <ProfitFirstCoach />

      {/* Tax reserve + Health score · 50/50 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <TaxReserveCard
          balance={tax.current_tax_balance}
          estimate={tax.quarterly_tax_estimate}
          healthPct={tax.reserve_health_pct}
          healthCapped={tax.reserve_health_pct_capped}
          status={tax.reserve_health_status}
          nextDue={tax.next_payment_due_estimate}
          daysToNext={tax.days_to_next_payment}
          quarter={tax.current_quarter}
        />
        <HealthScoreCard score={score} />
      </div>

      {/* Row 5 — Recent allocations */}
      <RecentAllocations allocations={allocations} />

      {/* Notes footer */}
      <Callout title="What is this tab?" tone="default">
        Profit First is an allocation discipline: every dollar of income is pre-routed into Profit, Owner Pay, Tax, and OpEx
        bank accounts at fixed percentages. <span className="font-semibold">CAP</span> = what Relay does today.
        <span className="font-semibold"> TAP</span> = the target this firm is working toward.
        Drift between the two is expected — it's the strategic distance we're closing as revenue grows.
        Data flows: Viktor (automation) writes daily balances + weekly P&L from QuickBooks into the sheet; this dashboard
        refreshes every 4 hours.
      </Callout>
    </div>
  );
}

// ---------------------------------------------------------------------------

function TierStrategyCard({ tier, tiers, currentTier, tapCap, taxReserve }) {
  // Tier letters in display order — matches Joey's Config sheet (A-F).
  const tierLetters = Object.keys(tiers || {}).sort();
  const allocatableRows = (tapCap || []).filter((r) => r.short_code !== 'Income');

  // Compute recommendations from drift + tier proximity. Returns up to 3 items.
  //
  // Doctrine: Profit First prescribes *small, incremental* CAP moves — typically
  // 1-2 percentage points per quarter — so OpEx has time to tighten and the firm
  // doesn't shock its cash flow. We use 2pp/quarter as the default step (the more
  // aggressive end of Mike's range) but recommendations cap at the actual drift
  // when smaller. Anything over a 4-quarter horizon shows a glide path so the
  // team can see the plan, not just the next step.
  const STEP_PP = 2;  // percentage-points per quarter
  const recommendations = useMemo(() => {
    const recs = [];

    // Build drift signal per non-Income account.
    const driftRows = allocatableRows
      .filter((r) => r.tap != null && r.cap != null && Math.abs(r.tap - r.cap) > 0.005)
      .map((r) => ({
        ...r,
        deltaPp: (r.tap - r.cap) * 100,  // sign tells direction
        absPp: Math.abs((r.tap - r.cap) * 100),
      }));

    // Helper: compute this-quarter target for a single account.
    const moveThisQuarter = (row) => {
      const step = Math.min(row.absPp, STEP_PP);
      const direction = row.deltaPp >= 0 ? 1 : -1;
      const newCap = row.cap + (direction * step) / 100;
      return {
        ...row,
        stepPp: step,
        direction,
        newCap,
        quartersToClose: Math.ceil(row.absPp / STEP_PP),
      };
    };

    // Helper: build a glide path string ("Q2 2026 → 73% · Q3 → 71% · ...").
    const glidePath = (row) => {
      const today = new Date();
      const startYear = today.getFullYear();
      const startQ = Math.floor(today.getMonth() / 3) + 1;
      const steps = Math.ceil(row.absPp / STEP_PP);
      const direction = row.deltaPp >= 0 ? 1 : -1;
      const path = [];
      let cap = row.cap * 100;
      for (let i = 0; i < steps; i += 1) {
        const stepSize = Math.min(STEP_PP, Math.abs(row.tap * 100 - cap));
        cap += direction * stepSize;
        // Round to avoid float noise like 64.99999...
        cap = Math.round(cap);
        const qNum = ((startQ - 1 + i) % 4) + 1;
        const yr = startYear + Math.floor((startQ - 1 + i) / 4);
        path.push({ label: `Q${qNum} ${yr}`, cap });
      }
      return path;
    };

    const upMoves = driftRows.filter((r) => r.deltaPp > 0).sort((a, b) => b.absPp - a.absPp);
    const downMoves = driftRows.filter((r) => r.deltaPp < 0).sort((a, b) => b.absPp - a.absPp);

    // PRIMARY: pair the biggest down-move (usually OpEx) with the biggest
    // up-moves so the math reconciles to 100%. The down step DRIVES the
    // total up-allocation — the sum of pp raised across up-movers must equal
    // the pp lowered on the down account, otherwise the four CAPs would no
    // longer total 100% in Relay. We distribute proportionally by drift and
    // cap each up-mover at its own drift so we never overshoot a TAP.
    if (downMoves.length > 0 && upMoves.length > 0) {
      const selectedUps = upMoves.slice(0, 2);
      const totalUpDrift = selectedUps.reduce((s, r) => s + r.absPp, 0);
      // Down step is capped by (a) the global 2pp pace, (b) the down account's
      // own drift, and (c) the sum of up-account drift (can't lower OpEx more
      // than the others can absorb in a single quarter).
      const downStepPp = Math.min(downMoves[0].absPp, STEP_PP, totalUpDrift);
      const downRow = downMoves[0];
      const down = {
        ...downRow,
        stepPp: downStepPp,
        direction: -1,
        newCap: downRow.cap - downStepPp / 100,
        quartersToClose: Math.ceil(downRow.absPp / STEP_PP),
      };

      // Distribute the down step across up-movers proportionally by drift,
      // capped at each account's drift. Any residual (from capping) goes to
      // the up-mover with the most remaining headroom — preserves the
      // 100%-sum invariant exactly.
      let remaining = downStepPp;
      const provisional = selectedUps.map((r) => {
        const share = totalUpDrift > 0
          ? (r.absPp / totalUpDrift) * downStepPp
          : downStepPp / selectedUps.length;
        const stepPp = Math.min(share, r.absPp);
        remaining -= stepPp;
        return { row: r, stepPp };
      });
      if (remaining > 1e-6) {
        let idx = -1;
        let maxHeadroom = 0;
        provisional.forEach((p, i) => {
          const headroom = p.row.absPp - p.stepPp;
          if (headroom > maxHeadroom) { maxHeadroom = headroom; idx = i; }
        });
        if (idx >= 0) {
          provisional[idx].stepPp += Math.min(remaining, maxHeadroom);
        }
      }

      const ups = provisional
        .filter((p) => p.stepPp > 1e-6)
        .map(({ row, stepPp }) => ({
          ...row,
          stepPp,
          direction: 1,
          newCap: row.cap + stepPp / 100,
          quartersToClose: Math.ceil(row.absPp / STEP_PP),
        }));

      const upTextThisQ = ups
        .map((u) => {
          const stepLabel = u.stepPp >= 1
            ? `+${u.stepPp.toFixed(u.stepPp % 1 < 0.05 ? 0 : 1)}pp`
            : `+${u.stepPp.toFixed(1)}pp`;
          return `${ACCOUNT_LABEL[u.short_code]} ${(u.cap * 100).toFixed(0)}% → ${(u.newCap * 100).toFixed(0)}% (${stepLabel})`;
        })
        .join(', ');
      const downStepLabel = downStepPp >= 1
        ? `${downStepPp.toFixed(downStepPp % 1 < 0.05 ? 0 : 1)}pp`
        : `${downStepPp.toFixed(1)}pp`;
      recs.push({
        kind: 'drift_quarterly',
        priority: 'primary',
        title: `This quarter: lower ${ACCOUNT_LABEL[down.short_code]} ${(down.cap * 100).toFixed(0)}% → ${(down.newCap * 100).toFixed(0)}% (a ${downStepLabel} step)`,
        body: `Profit First prescribes small, sustainable moves — up to ${STEP_PP} percentage points per quarter so OpEx spend has time to tighten. To keep the four CAPs at 100%, the ${downStepLabel} you pull from ${ACCOUNT_LABEL[down.short_code]} gets split across the underfunded accounts: raise ${upTextThisQ} in Relay. Audit vendors, subscriptions, and contractor scope first; the CAP change should follow real spend reductions, not lead them.`,
        glidePath: glidePath(downRow),
        glidePathLabel: `${ACCOUNT_LABEL[down.short_code]} glide path to ${(down.tap * 100).toFixed(0)}% TAP`,
        quartersToClose: down.quartersToClose,
      });
    } else if (upMoves.length > 0) {
      const u = moveThisQuarter(upMoves[0]);
      recs.push({
        kind: 'drift_quarterly',
        priority: 'primary',
        title: `This quarter: raise ${ACCOUNT_LABEL[u.short_code]} ${(u.cap * 100).toFixed(0)}% → ${(u.newCap * 100).toFixed(0)}%`,
        body: `${STEP_PP}pp step toward the ${(u.tap * 100).toFixed(0)}% TAP. Adjust in Relay; the next quarter's reading determines whether to step again or hold.`,
        glidePath: glidePath(u),
        glidePathLabel: `${ACCOUNT_LABEL[u.short_code]} glide path to ${(u.tap * 100).toFixed(0)}% TAP`,
        quartersToClose: u.quartersToClose,
      });
    } else if (downMoves.length > 0) {
      const d = moveThisQuarter(downMoves[0]);
      recs.push({
        kind: 'drift_quarterly',
        priority: 'primary',
        title: `This quarter: lower ${ACCOUNT_LABEL[d.short_code]} ${(d.cap * 100).toFixed(0)}% → ${(d.newCap * 100).toFixed(0)}%`,
        body: `${STEP_PP}pp step toward the ${(d.tap * 100).toFixed(0)}% TAP. Tighten the operational spend first, then update Relay.`,
        glidePath: glidePath(d),
        glidePathLabel: `${ACCOUNT_LABEL[d.short_code]} glide path to ${(d.tap * 100).toFixed(0)}% TAP`,
        quartersToClose: d.quartersToClose,
      });
    }

    // SECONDARY (tax sweep): the Tax account holds money earmarked for the
    // next quarterly estimated payment. When recent revenue, allocation
    // overshoot, or a windfall pushes it well above that liability + a small
    // safety buffer, the excess sits idle. Mike's doctrine: when Tax is
    // overfunded, the windfall belongs to Profit (reward for work already
    // done). Exception: if OpEx CAP is materially BELOW its TAP — meaning
    // the operating account is genuinely starved of allocation — divert the
    // sweep to OpEx instead.
    const taxBalance = taxReserve?.current_tax_balance || 0;
    const taxEstimate = taxReserve?.quarterly_tax_estimate || 0;
    const SAFETY_BUFFER_PCT = 0.10;
    const taxNeed = taxEstimate * (1 + SAFETY_BUFFER_PCT);
    const taxExcess = taxBalance - taxNeed;
    // Find OpEx row to check if it's genuinely under-allocated (CAP < TAP).
    const opexRow = allocatableRows.find((r) => r.short_code === 'OpEx');
    // Positive = OpEx CAP below TAP (under-allocated → operating cash starved).
    const opexUnderByPp = opexRow ? (opexRow.tap - opexRow.cap) * 100 : 0;
    const opexUnderfunded = opexUnderByPp > 5; // OpEx CAP > 5pp below TAP
    if (taxEstimate > 0 && taxExcess > 1000) {
      // Round sweep down to nearest $100 so the suggestion lands cleanly.
      const sweepAmount = Math.floor(taxExcess / 100) * 100;
      const target = opexUnderfunded ? 'OpEx' : 'Profit';
      const rationale = opexUnderfunded
        ? `OpEx CAP is ${opexUnderByPp.toFixed(0)}pp below its TAP — operations are starved of allocation, so this surplus can shore up the operating account.`
        : `Per Mike Michalowicz: when Tax is overfunded, the excess belongs to Profit — it rewards the work the firm has already done.`;
      recs.push({
        kind: 'tax_sweep',
        priority: 'attention',
        title: `Tax reserve is overfunded by ~${moneyFull(taxExcess)} — consider a 1-time sweep of ${moneyFull(sweepAmount)} to ${target}`,
        body: `Tax balance (${moneyFull(taxBalance)}) is above the next quarterly liability (${moneyFull(taxEstimate)}) plus a 10% safety cushion (${moneyFull(taxNeed)}). ${rationale} Wait until the next quarterly payment clears before sweeping — that confirms the liability is settled and the surplus is real.`,
      });
    }

    // SECONDARY: tier-boundary recommendations.
    const ttm = tier.ttm_real_revenue || 0;
    const nextLetter = tier.next_tier;
    const nextFloor = tier.next_tier_floor;
    const tierProgress = tier.progress_pct || 0;

    if (nextLetter && nextFloor && ttm >= nextFloor) {
      recs.push({
        kind: 'tier_crossed',
        priority: 'attention',
        title: `TTM revenue is in Tier ${nextLetter} territory — update Config`,
        body: `TTM real revenue (${moneyFull(ttm)}) is already above the Tier ${nextLetter} floor (${moneyFull(nextFloor)}). Switch Config.current_tier in the sheet so TAPs adapt to the new band.`,
      });
    } else if (nextLetter && tierProgress >= 0.85) {
      const changes = tier.tap_changes_at_next_tier || [];
      const changeText = changes
        .map((c) => `${c.account} TAP ${(c.from_pct * 100).toFixed(0)}% → ${(c.to_pct * 100).toFixed(0)}%`)
        .join(', ');
      recs.push({
        kind: 'tier_approaching',
        priority: 'attention',
        title: `Approaching Tier ${nextLetter} — start planning the TAP shift`,
        body: `You're ${Math.round(tierProgress * 100)}% through the Tier ${tier.tier} band. At Tier ${nextLetter} (${moneyFull(nextFloor)}+): ${changeText}. Begin trending CAPs toward the new TAPs before the threshold hits.`,
      });
    }

    // FALLBACK: healthy-state recommendation.
    if (recs.length === 0) {
      recs.push({
        kind: 'healthy',
        priority: 'positive',
        title: 'CAPs are tracking TAPs — no changes recommended this quarter',
        body: `Every account is within 0.5pp of its target. Hold steady. Revisit next quarter or when TTM approaches the Tier ${tier.next_tier || 'next'} threshold (${moneyFull(tier.next_tier_floor)}).`,
      });
    }

    return recs;
  }, [tier, allocatableRows, taxReserve]);

  return (
    <div data-testid="pf-tier-strategy" className="bg-white border-t-4 border-indie-navy p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-5">
        <div>
          <div className="label-editorial">Tier &amp; allocation strategy</div>
          <div className="flex items-baseline gap-3 mt-2">
            <div className="text-3xl lg:text-4xl font-bold text-indie-navy tracking-tight">Tier {tier.tier || '—'}</div>
            <div className="text-sm text-indie-ink/65">
              {moneyFull(tier.tier_floor)} – {moneyFull(tier.tier_ceiling)} real revenue band
            </div>
          </div>
          <div className="text-sm text-indie-ink/70 mt-1.5">
            TTM real revenue: <span className="font-semibold text-indie-navy num-tabular">{moneyFull(tier.ttm_real_revenue)}</span>
            {tier.next_tier && (
              <span className="text-indie-ink/55 ml-2">
                · next tier {tier.next_tier} at <span className="num-tabular">{moneyFull(tier.next_tier_floor)}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Per-account CAP vs TAP bars */}
      <div className="space-y-3.5 mb-6">
        {allocatableRows.map((r) => (
          <CapTapBar key={r.short_code} row={r} />
        ))}
      </div>

      {/* Recommendations */}
      <div className="bg-indie-cream border-t-2 border-indie-yellow p-5 mb-6">
        <div className="label-editorial mb-3">Recommendations</div>
        <div className="space-y-3">
          {recommendations.map((rec, i) => (
            <RecommendationItem key={i} rec={rec} />
          ))}
        </div>
      </div>

      {/* Reference chart — always visible per Joey's request */}
      <div data-testid="pf-tier-reference">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen size={13} className="text-indie-cobalt" />
          <div className="label-editorial">Profit First reference</div>
        </div>
        <p className="text-xs text-indie-ink/65 leading-relaxed mb-3">
          Mike Michalowicz's Profit First framework prescribes TAP percentages by real-revenue tier.
          As revenue grows, Profit % rises and OpEx % tightens.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs num-tabular border border-indie-navy/15">
            <thead>
              <tr className="bg-indie-navy text-indie-cream">
                <th className="text-left px-2.5 py-2 font-medium tracking-wider uppercase text-[10px]">Tier</th>
                <th className="text-left px-2.5 py-2 font-medium tracking-wider uppercase text-[10px]">Real revenue</th>
                <th className="text-right px-2.5 py-2 font-medium tracking-wider uppercase text-[10px]">Profit</th>
                <th className="text-right px-2.5 py-2 font-medium tracking-wider uppercase text-[10px]">Owner</th>
                <th className="text-right px-2.5 py-2 font-medium tracking-wider uppercase text-[10px]">Tax</th>
                <th className="text-right px-2.5 py-2 font-medium tracking-wider uppercase text-[10px]">OpEx</th>
              </tr>
            </thead>
            <tbody>
              {tierLetters.map((letter) => {
                const t = tiers[letter];
                const active = letter === currentTier;
                return (
                  <tr key={letter} className={active ? 'bg-indie-yellow/30 font-semibold' : 'odd:bg-white even:bg-indie-cream/30'}>
                    <td className="px-2.5 py-1.5 text-indie-navy">{letter}{active ? ' ← you' : ''}</td>
                    <td className="px-2.5 py-1.5 text-indie-ink/75">
                      {moneyFull(t.real_revenue_floor)} – {moneyFull(t.real_revenue_ceiling)}
                    </td>
                    <td className="text-right px-2.5 py-1.5">{t.tap_profit != null ? pct(t.tap_profit, 0) : '—'}</td>
                    <td className="text-right px-2.5 py-1.5">{t.tap_owner != null ? pct(t.tap_owner, 0) : '—'}</td>
                    <td className="text-right px-2.5 py-1.5">{t.tap_tax != null ? pct(t.tap_tax, 0) : '—'}</td>
                    <td className="text-right px-2.5 py-1.5">{t.tap_opex != null ? pct(t.tap_opex, 0) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// One row showing CAP (current Relay allocation) vs TAP (target) as two stacked
// horizontal bars. Visual gap = the journey to close in Relay.
function CapTapBar({ row }) {
  const capPct = (row.cap || 0) * 100;
  const tapPct = (row.tap || 0) * 100;
  const deltaPp = tapPct - capPct;
  const onTarget = Math.abs(deltaPp) < 0.5;
  const direction = onTarget ? 'on target' : deltaPp > 0 ? `+${deltaPp.toFixed(0)}pp to raise` : `${deltaPp.toFixed(0)}pp to lower`;
  const dotColor = ACCOUNT_SPARK[row.short_code] || '#1D57A5';

  return (
    <div data-testid={`pf-cap-tap-${row.short_code.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: dotColor }} />
          <span className="font-semibold text-indie-navy">{ACCOUNT_LABEL[row.short_code]}</span>
        </div>
        <div className="text-xs uppercase tracking-wider num-tabular">
          <span className={onTarget ? 'text-indie-evergreen' : 'text-indie-cobalt'}>
            {direction}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-[80px_1fr_60px] items-center gap-3 text-xs">
        <span className="text-indie-ink/55 uppercase tracking-wider text-[10px]">CAP today</span>
        <div className="h-3 bg-indie-cream relative overflow-hidden">
          <div className="absolute top-0 left-0 h-3" style={{ width: `${Math.min(capPct, 100)}%`, background: dotColor, opacity: 0.55 }} />
        </div>
        <span className="text-right font-semibold text-indie-navy num-tabular">{capPct.toFixed(0)}%</span>
      </div>
      <div className="grid grid-cols-[80px_1fr_60px] items-center gap-3 text-xs mt-1.5">
        <span className="text-indie-ink/55 uppercase tracking-wider text-[10px]">TAP target</span>
        <div className="h-3 bg-indie-cream relative overflow-hidden border-l border-r border-indie-navy/10">
          <div className="absolute top-0 left-0 h-3" style={{ width: `${Math.min(tapPct, 100)}%`, background: dotColor }} />
        </div>
        <span className="text-right font-semibold text-indie-navy num-tabular">{tapPct.toFixed(0)}%</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function RecommendationItem({ rec }) {
  const palette = {
    primary: { dot: 'bg-indie-cobalt', label: 'ACTION' },
    attention: { dot: 'bg-indie-yellow', label: 'HEADS UP' },
    positive: { dot: 'bg-indie-evergreen', label: 'STEADY' },
  }[rec.priority] || { dot: 'bg-indie-navy', label: 'NOTE' };
  return (
    <div className="bg-white border-l-4 border-indie-navy/15 pl-3 py-2">
      <div className="flex items-baseline gap-2 mb-1">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${palette.dot}`} />
        <span className="text-[10px] uppercase tracking-wider font-semibold text-indie-ink/55">{palette.label}</span>
        {rec.quartersToClose && (
          <span className="text-[10px] uppercase tracking-wider text-indie-ink/45 ml-auto">
            ~{rec.quartersToClose} {rec.quartersToClose === 1 ? 'quarter' : 'quarters'} to target
          </span>
        )}
      </div>
      <div className="font-semibold text-indie-navy text-sm leading-snug">{rec.title}</div>
      <div className="text-xs text-indie-ink/70 mt-1 leading-relaxed">{rec.body}</div>
      {rec.glidePath && rec.glidePath.length > 0 && (
        <div className="mt-3 bg-indie-cream/40 p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-indie-ink/55 mb-1.5">{rec.glidePathLabel}</div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs num-tabular">
            {rec.glidePath.map((step, idx) => (
              <React.Fragment key={`${step.label}-${idx}`}>
                <span className="text-indie-ink/65">
                  <span className="text-indie-ink/50">{step.label}:</span> <span className="font-semibold text-indie-navy">{step.cap}%</span>
                </span>
                {idx < rec.glidePath.length - 1 && <ArrowRight size={10} className="text-indie-ink/30" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

// Revenue context — explains the two top-of-sheet revenue numbers and how
// Profit First uses TTM (not forecast) to position the firm in a tier.
function RevenueContextCard({ forecast, ttm, currentTier, tierFloor, tierCeiling, nextTierFloor, nextTierLetter, taxRate }) {
  const variancePct = (forecast && ttm) ? (forecast - ttm) / ttm : null;
  const ttmShareOfForecast = (forecast && ttm) ? ttm / forecast : null;
  const headroomToNextTier = (nextTierFloor && ttm) ? nextTierFloor - ttm : null;

  return (
    <div data-testid="pf-revenue-context" className="bg-white border-t-4 border-indie-cobalt p-6 lg:p-8">
      <div className="label-editorial">Revenue context</div>
      <div className="text-xl font-semibold text-indie-navy mt-1 mb-5">Two different numbers — and why they each matter</div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Forecast */}
        <div data-testid="pf-rev-forecast" className="bg-indie-cream/60 border-l-4 border-indie-navy p-4">
          <div className="text-[10px] uppercase tracking-wider text-indie-ink/60">FY forecast</div>
          <div className="text-3xl font-bold text-indie-navy num-tabular tracking-tight mt-1">{moneyFull(forecast)}</div>
          <div className="text-xs text-indie-ink/70 mt-2 leading-relaxed">
            This year's <span className="font-semibold">revenue goal</span> — the planning number flowing from your BHAG and operating plan.
            It powers the rest of the Command Center (P&L forecast, cash runway, BHAG progress).
          </div>
        </div>

        {/* TTM */}
        <div data-testid="pf-rev-ttm" className="bg-indie-cream/60 border-l-4 border-indie-evergreen p-4">
          <div className="text-[10px] uppercase tracking-wider text-indie-ink/60">TTM real revenue</div>
          <div className="text-3xl font-bold text-indie-evergreen num-tabular tracking-tight mt-1">{moneyFull(ttm)}</div>
          <div className="text-xs text-indie-ink/70 mt-2 leading-relaxed">
            <span className="font-semibold">Trailing twelve months</span> of actual real revenue (gross minus pass-through costs).
            This is the number Profit First uses to place you in a tier — not the forecast. You operate on what you've earned, not what you hope to earn.
          </div>
        </div>
      </div>

      {/* Explainer paragraph */}
      <div className="bg-indie-cream border-t-2 border-indie-yellow p-5 text-sm text-indie-ink/80 leading-relaxed space-y-2">
        <p>
          <span className="font-semibold text-indie-navy">How they relate:</span> your TTM sits at{' '}
          <span className="num-tabular font-semibold">{moneyFull(ttm)}</span>
          {ttmShareOfForecast != null && (
            <> — about <span className="num-tabular font-semibold">{pct(ttmShareOfForecast, 0)}</span> of your{' '}
            <span className="num-tabular">{moneyFull(forecast)}</span> FY forecast</>
          )}.
          {variancePct != null && Math.abs(variancePct) > 0.05 && (
            <>{' '}If you hit the forecast, TTM will climb roughly{' '}
              <span className="num-tabular font-semibold">{pct(variancePct, 0)}</span> by year-end as older lower months roll off.
            </>
          )}
        </p>
        <p>
          <span className="font-semibold text-indie-navy">Why two numbers:</span> the forecast tells you where you're <em>going</em> (and runs your cash plan).
          TTM tells you where you <em>are</em> — and that's what dictates which Profit First tier the firm operates in today.
          Tier <span className="font-semibold">{currentTier || '—'}</span> runs from{' '}
          <span className="num-tabular">{moneyFull(tierFloor)}</span> to <span className="num-tabular">{moneyFull(tierCeiling)}</span>.
          {nextTierLetter && headroomToNextTier != null && headroomToNextTier > 0 && (
            <> You need another <span className="num-tabular font-semibold">{moneyFull(headroomToNextTier)}</span> of TTM real revenue before Tier {nextTierLetter} kicks in and Profit/OpEx TAPs reset.</>
          )}
        </p>
        {taxRate != null && (
          <p className="text-xs text-indie-ink/65 pt-1">
            <span className="font-semibold text-indie-navy">One more thing:</span> the {pct(taxRate, 0)} quarterly tax rate you set in the sheet
            is applied against <em>quarterly taxable income</em>, not real revenue. It's the input to your Tax reserve estimate — independent of TAP percentages.
          </p>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------

// On-demand Claude-powered coaching note written in Mike Michalowicz's voice.
// Latest cached analysis loads on mount; user clicks "Refresh" to spend tokens
// generating a new one from the current Profit First state.
function ProfitFirstCoach() {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get('/ai/profit-first/latest')
      .then((r) => setAnalysis(r.data))
      .catch(() => { /* swallow — first-time empty is normal */ })
      .finally(() => setLoading(false));
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await api.post('/ai/profit-first');
      setAnalysis(r.data);
      toast.success('Fresh Mike-mode note generated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate analysis.');
    } finally {
      setGenerating(false);
    }
  };

  const generated = analysis?.created_at
    ? new Date(analysis.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  // Stale-flag: gentle nudge if cached note is >7 days old, urgent if >30.
  const daysOld = useMemo(() => {
    if (!analysis?.created_at) return null;
    const h = relativeHours(analysis.created_at);
    return h == null ? null : h / 24;
  }, [analysis]);
  const staleLevel = daysOld == null ? null : daysOld > 30 ? 'urgent' : daysOld > 7 ? 'gentle' : null;

  return (
    <div data-testid="pf-coach" className="bg-white border-t-4 border-indie-evergreen p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} className="text-indie-evergreen" />
            <div className="label-editorial">AI coach · Claude Sonnet 4.5</div>
          </div>
          <div className="text-xl font-semibold text-indie-navy mt-1">What would Mike Michalowicz say?</div>
          <div className="text-xs text-indie-ink/60 mt-1">
            Reads the firm's full Profit First state and writes a coaching note in Mike's voice. Costs ~$0.05 per generation.
          </div>
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          data-testid="pf-coach-generate"
          className="no-print inline-flex items-center gap-2 bg-indie-evergreen text-white px-3 py-2 text-xs uppercase tracking-wider font-semibold hover:bg-indie-evergreen/90 disabled:opacity-50"
        >
          <RefreshCw size={13} className={generating ? 'animate-spin' : ''} />
          {generating ? 'Thinking…' : analysis ? 'Refresh analysis' : 'Generate analysis'}
        </button>
      </div>

      {loading && (
        <div className="text-sm text-indie-ink/55 italic py-6">Loading latest coaching note…</div>
      )}

      {!loading && !analysis && !generating && (
        <div className="bg-indie-cream border-l-4 border-indie-yellow px-4 py-3 text-sm text-indie-ink/75 leading-relaxed">
          No coaching note yet. Click <span className="font-semibold">Generate analysis</span> to get Mike's first read on your current Profit First state.
        </div>
      )}

      {generating && !analysis && (
        <div className="text-sm text-indie-ink/55 italic py-6 flex items-center gap-2">
          <RefreshCw size={14} className="animate-spin text-indie-evergreen" />
          Claude is reading your numbers and channeling Mike. Usually 15-30 seconds…
        </div>
      )}

      {!loading && analysis && (
        <>
          {staleLevel && (
            <div
              data-testid="pf-coach-stale-banner"
              className={`mb-4 flex items-start gap-3 px-4 py-3 text-sm leading-relaxed ${
                staleLevel === 'urgent'
                  ? 'bg-indie-neg-light/40 border-l-4 border-indie-neg-dark text-indie-neg-dark'
                  : 'bg-indie-yellow/20 border-l-4 border-indie-yellow text-indie-ink'
              }`}
            >
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">
                  {staleLevel === 'urgent' ? 'This note is over 30 days old.' : 'This note may be stale.'}
                </span>{' '}
                Generated {Math.round(daysOld)} days ago — your numbers have moved since then. Click <span className="font-semibold">Refresh analysis</span> to regenerate with the latest data.
              </div>
            </div>
          )}
          <div data-testid="pf-coach-narrative" className="bg-indie-cream/40 px-5 py-4 border-l-4 border-indie-evergreen">
            <AiMarkdown>{analysis.narrative || ''}</AiMarkdown>
          </div>
          {generated && (
            <div className="mt-3 text-[11px] text-indie-ink/50 tracking-wide">
              Generated {generated} · based on Tier {analysis.tier || '—'}, TTM {moneyFull(analysis.ttm_real_revenue)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function TaxReserveCard({ balance, estimate, healthPct, healthCapped, status, nextDue, daysToNext, quarter }) {
  // Status copy parses Viktor's emoji-prefixed strings. Falls back to neutral.
  const statusLabel = status || '';
  let tone = 'cobalt';
  let Icon = ShieldCheck;
  if (statusLabel.includes('Funded') || statusLabel.includes('✅')) { tone = 'evergreen'; Icon = CheckCircle2; }
  else if (statusLabel.includes('Close') || statusLabel.includes('⚠')) { tone = 'yellow'; Icon = AlertTriangle; }
  else if (statusLabel.includes('Short') || statusLabel.includes('🚨')) { tone = 'red'; Icon = AlertOctagon; }
  const toneCls = {
    evergreen: 'text-indie-evergreen border-indie-evergreen',
    yellow: 'text-indie-yellow border-indie-yellow',
    red: 'text-indie-neg-dark border-indie-neg-dark',
    cobalt: 'text-indie-cobalt border-indie-cobalt',
  }[tone];

  // Cap displayed % at 100%+ (over-funding doesn't earn extra display credit;
  // raw values are misleading early in a quarter when QTD income is tiny).
  const displayPct = healthPct == null ? '—' : (healthCapped ? '100%+' : `${(healthPct * 100).toFixed(0)}%`);
  const fillPct = Math.min(Math.max((healthPct || 0) * 100, 0), 100);
  const showEarlyQuarterNote = healthPct != null && healthPct > 2;

  return (
    <div data-testid="pf-tax-reserve" className={`bg-white border-t-4 ${toneCls.split(' ')[1]} p-6`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="label-editorial">Tax reserve health</div>
          <div className="text-xs text-indie-ink/60 mt-1">{quarter || 'Current quarter'}</div>
        </div>
        <Icon className={toneCls.split(' ')[0]} size={22} />
      </div>

      <div className={`text-4xl font-bold num-tabular tracking-tight ${toneCls.split(' ')[0]}`}>
        {displayPct}
      </div>
      <div className={`text-sm font-semibold mt-1 ${toneCls.split(' ')[0]}`}>{statusLabel || '—'}</div>

      <div className="mt-4 h-2 bg-indie-cream">
        <div className={`h-2 bg-current ${toneCls.split(' ')[0]}`} style={{ width: `${fillPct}%` }} />
      </div>

      <div className="mt-4 space-y-1.5 text-sm num-tabular">
        <div className="flex justify-between">
          <span className="text-indie-ink/65">Balance</span>
          <span className="text-indie-navy font-semibold">{moneyFull(balance)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-indie-ink/65">Q-estimate</span>
          <span className="text-indie-ink">{moneyFull(estimate)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-indie-ink/65">Next payment</span>
          <span className="text-indie-ink">{nextDue || '—'}{daysToNext != null ? ` · ${daysToNext}d` : ''}</span>
        </div>
      </div>

      {showEarlyQuarterNote && (
        <div className="mt-4 pt-3 border-t border-indie-navy/10 text-[11px] text-indie-ink/55 leading-relaxed">
          Early in the quarter — the estimate is small because QTD income is small. The ratio matures as the quarter accrues.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------


function HealthScoreCard({ score }) {
  const s = score.score || 0;
  const label = score.label || '—';
  const labelTone = label === 'Strong' ? 'text-indie-evergreen' : label === 'Watch' ? 'text-indie-yellow' : 'text-indie-neg-dark';
  const components = score.components || {};
  const weights = score.weights || {};

  const subMetrics = [
    { key: 'tax_reserve', label: 'Tax reserve health' },
    { key: 'allocation_execution', label: 'Allocation execution (last 8w)' },
    { key: 'drift_toward_tap', label: 'Drift toward TAP' },
    { key: 'data_freshness', label: 'Data freshness' },
  ];

  return (
    <div data-testid="pf-health-score" className="bg-white border-t-4 border-indie-evergreen p-6">
      <div className="flex items-start justify-between mb-3">
        <div className="label-editorial">Profit health score</div>
        <Activity className="text-indie-evergreen" size={22} />
      </div>
      <div className="flex items-baseline gap-3">
        <div className="text-5xl font-bold text-indie-evergreen num-tabular tracking-tight">{s}</div>
        <div className="text-indie-ink/50 text-sm num-tabular">/ 100</div>
        <div className={`ml-auto text-sm font-semibold uppercase tracking-wider ${labelTone}`}>{label}</div>
      </div>

      <div className="mt-5 space-y-2.5">
        {subMetrics.map((m) => {
          const v = components[m.key] ?? 0;
          const w = weights[m.key] ?? 0;
          return (
            <div key={m.key}>
              <div className="flex items-baseline justify-between mb-1 text-xs">
                <span className="text-indie-ink/75">{m.label}</span>
                <span className="num-tabular text-indie-ink/60">{v} <span className="text-indie-ink/40 text-[10px] ml-1">· weight {w}</span></span>
              </div>
              <div className="h-1.5 bg-indie-cream">
                <div className="h-1.5 bg-indie-evergreen/70" style={{ width: `${v}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function RecentAllocations({ allocations }) {
  return (
    <div data-testid="pf-recent-allocations" className="bg-white border-t-4 border-indie-navy">
      <div className="px-6 pt-5 pb-3 flex items-center justify-between">
        <div>
          <div className="label-editorial">Recent allocations</div>
          <div className="text-xl font-semibold text-indie-navy mt-1">Last 8 Monday sweeps</div>
        </div>
      </div>
      {allocations.length === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-indie-ink/55 italic">
          Awaiting Tuesday sync — Viktor logs Monday sweeps every Tuesday at 7 AM CT.
        </div>
      ) : (
        <table className="w-full text-sm num-tabular">
          <thead>
            <tr className="bg-indie-navy text-indie-cream">
              <th className="text-left px-4 py-3 font-medium tracking-wider uppercase text-xs">Date</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">Income swept</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">→ Profit</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">→ Owner</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">→ Tax</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">→ OpEx</th>
            </tr>
          </thead>
          <tbody>
            {allocations.map((a, i) => (
              <tr key={a.allocation_date} className={i % 2 === 0 ? 'bg-white' : 'bg-indie-cream/40'}>
                <td className="px-4 py-2.5 font-medium text-indie-navy">{a.allocation_date}</td>
                <td className="text-right px-4 py-2.5">{moneyFull(a.income_swept)}</td>
                <td className="text-right px-4 py-2.5 text-indie-evergreen">{moneyFull(a.to_profit)}</td>
                <td className="text-right px-4 py-2.5 text-indie-cobalt">{moneyFull(a.to_owner)}</td>
                <td className="text-right px-4 py-2.5">{moneyFull(a.to_tax)}</td>
                <td className="text-right px-4 py-2.5">{moneyFull(a.to_opex)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
